import { HazmatSchema } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import {
  LengthSchema,
  MoneySchema,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { CustomParcelType } from "./parcel";

const outboundShipmentParcelItemV2Schema = z
  .object({
    item_identifier: z.string().meta({ privacy: privacy.nonPii }),
    description: z.string().meta({ privacy: privacy.productCatalogText }),
    quantity: z.number().int().positive().meta({ privacy: privacy.nonPii }),
    unit_value: MoneySchema.meta({ privacy: privacy.structured }),
    unit_weight: WeightSchema.meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

export interface OutboundShipmentParcelItemV2 extends z.infer<
  typeof outboundShipmentParcelItemV2Schema
> {}

// Unlike the V1 parcel, V2 parcels declare their own units and carry no customs
// info (customs is shipment-level in V2). Kept in its own leaf module so the
// rate schema (outbound-labels) can reference it without a cycle through
// outbound-shipment.
const outboundShipmentParcelV2Schema = z
  .object({
    length: LengthSchema.nullish().meta({ privacy: privacy.structured }),
    width: LengthSchema.nullish().meta({ privacy: privacy.structured }),
    height: LengthSchema.nullish().meta({ privacy: privacy.structured }),
    weight: WeightSchema.meta({ privacy: privacy.structured }),
    items: z
      .array(outboundShipmentParcelItemV2Schema)
      .nullish()
      .meta({ privacy: privacy.structured }),
    predefined_package: z.string().nullish().meta({ privacy: privacy.nonPii }),
    parcelType: createEnum(CustomParcelType, { enumName: "CustomParcelType" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    additionalHandlingIndicator: z
      .boolean()
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    largePackageIndicator: z
      .boolean()
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    shipperReleaseIndicator: z
      .boolean()
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    // Hazmat and dry ice are per parcel in V2 (V1 carried them at the shipment
    // level). dry_ice_weight is unit-aware ({ unit, value }).
    hazmat: HazmatSchema.nullish().meta({ privacy: privacy.nonPii }),
    dry_ice: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
    dry_ice_weight: WeightSchema.nullish().meta({
      privacy: privacy.structured,
    }),
  })
  .meta({ privacy: privacy.structured });

export const OutboundShipmentParcelV2Schema = outboundShipmentParcelV2Schema;

export interface OutboundShipmentParcelV2 extends z.infer<
  typeof outboundShipmentParcelV2Schema
> {}
